/* =============================================================================
   STUDIO — theme behaviour
   - Vanilla JS, no dependencies. IIFE, strict mode.
   - Idempotent inits (safe with Theme Editor section reloads).
   - Cart uses the Section Rendering API: money & copy are rendered by Liquid,
     so everything is locale- and Markets-correct (no client-side formatting).
   ========================================================================== */
(function () {
  'use strict';

  var S = window.studio || (window.studio = {});
  var ROUTES = S.routes || {};
  var STRINGS = S.strings || {};
  var CART = S.cart || {};
  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- utils */
  function debounce(fn, delay) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, delay);
    };
  }
  function throttle(fn, limit) {
    var last = 0;
    return function () {
      var now = Date.now();
      if (now - last >= limit) { last = now; fn.apply(this, arguments); }
    };
  }
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function once(el, key) {
    if (!el) return false;
    if (el.dataset[key]) return false;
    el.dataset[key] = 'true';
    return true;
  }

  /* ----------------------------------------------------- a11y live region */
  function announce(msg) {
    if (!msg) return;
    var region = $('.studio-a11y-announce');
    if (!region) return;
    region.textContent = '';
    // Force re-announcement even for identical strings.
    window.setTimeout(function () { region.textContent = msg; }, 50);
  }

  /* ----------------------------------------------------------- focus trap */
  var FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';
  function focusable(container) {
    return $all(FOCUSABLE, container).filter(function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
    });
  }
  function FocusTrap(container) {
    this.container = container;
    this.opener = null;
    this.handler = this.onKeydown.bind(this);
  }
  FocusTrap.prototype.activate = function (opener) {
    this.opener = opener || document.activeElement;
    document.addEventListener('keydown', this.handler);
    var f = focusable(this.container);
    var target = this.container.querySelector('[autofocus]') || f[0] || this.container;
    window.requestAnimationFrame(function () { target.focus({ preventScroll: true }); });
  };
  FocusTrap.prototype.deactivate = function () {
    document.removeEventListener('keydown', this.handler);
    if (this.opener && typeof this.opener.focus === 'function') {
      this.opener.focus({ preventScroll: true });
    }
  };
  FocusTrap.prototype.onKeydown = function (e) {
    if (e.key !== 'Tab') return;
    // carrello-a11y-2110: contenitore ridisegnato e tolto dalla pagina → il recinto non blocca più il Tab
    if (!this.container.isConnected) return;
    var f = focusable(this.container);
    if (!f.length) { e.preventDefault(); return; }
    var first = f[0], last = f[f.length - 1];
    // fuoco fuori dal contenitore (es. sul body dopo un aggiornamento): si rientra dal primo o dall'ultimo elemento
    if (!this.container.contains(document.activeElement)) { e.preventDefault(); (e.shiftKey ? last : first).focus(); return; }
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  /* ------------------------------------------------------ scroll locking */
  var scrollLocks = 0;
  function lockScroll() { scrollLocks++; document.documentElement.classList.add('studio-scroll-lock'); }
  function unlockScroll() { scrollLocks = Math.max(0, scrollLocks - 1); if (scrollLocks === 0) document.documentElement.classList.remove('studio-scroll-lock'); }

  /* =========================================================== HEADER */
  function initHeader(root) {
    var header = $('.studio-header', root);
    if (!header || !once(header, 'studioHeaderBound')) return;
    var onScroll = throttle(function () {
      header.classList.toggle('studio-header--scrolled', window.scrollY > 50);
    }, 100);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ======================================================== ACCORDION */
  // pdp2-0210: apertura e chiusura con l'altezza misurata (Web Animations API) al posto della transizione su
  // grid-template-rows: meno lavoro per fotogramma sui telefoni e nessun salto alla fine. Senza animate() o con
  // "riduci movimento" resta il comportamento di prima (solo classe + CSS).
  var accordionReduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  function setAccordionItem(item, open) {
    var body = item.querySelector('.studio-accordion__body');
    if (!body || typeof body.animate !== 'function' || accordionReduce) {
      item.classList.toggle('studio-accordion__item--open', open);
      return;
    }
    if (body._studioAnim) { body._studioAnim.cancel(); body._studioAnim = null; }
    var from = body.getBoundingClientRect().height;
    body.style.transition = 'none';
    item.classList.add('studio-accordion__item--open');
    var to = open ? body.getBoundingClientRect().height : 0;
    var done = function () {
      if (!open) item.classList.remove('studio-accordion__item--open');
      body.style.overflow = '';
      // pdp3-0210: la transizione CSS del tema resta spenta. Riattivarla nel fotogramma dopo faceva ripartire
      // grid-template-rows 1fr → 0fr a chiusura finita: l'accordion si riapriva e richiudeva (doppia animazione).
    };
    if (Math.abs(to - from) < 1) { done(); return; }
    body.style.overflow = 'hidden';
    var anim = body.animate([{ height: from + 'px' }, { height: to + 'px' }], { duration: 280, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'forwards' });
    body._studioAnim = anim;
    anim.onfinish = function () {
      body._studioAnim = null;
      done();
      anim.cancel();
    };
  }
  function initAccordion(root) {
    $all('.studio-accordion', root).forEach(function (accordion) {
      if (!once(accordion, 'studioAccordionBound')) return;
      accordion.addEventListener('click', function (e) {
        var trigger = e.target.closest('.studio-accordion__trigger');
        if (!trigger || !accordion.contains(trigger)) return;
        var item = trigger.closest('.studio-accordion__item');
        var isOpen = item.classList.contains('studio-accordion__item--open');
        if (accordion.dataset.single !== 'false') {
          $all('.studio-accordion__item--open', accordion).forEach(function (openItem) {
            if (openItem !== item) {
              setAccordionItem(openItem, false);
              var t = openItem.querySelector('.studio-accordion__trigger');
              if (t) t.setAttribute('aria-expanded', 'false');
            }
          });
        }
        setAccordionItem(item, !isOpen);
        trigger.setAttribute('aria-expanded', String(!isOpen));
      });
    });
  }

  /* ========================================================== SLIDERS */
  function initSliders(root) {
    $all('[data-studio-slider]', root).forEach(function (container) {
      if (!once(container, 'studioSliderBound')) return;
      var row = $('.studio-slider-row', container);
      if (!row) return;
      var prev = $('[data-slider-prev]', container);
      var next = $('[data-slider-next]', container);
      function update() {
        if (prev) prev.disabled = row.scrollLeft <= 1;
        if (next) next.disabled = row.scrollLeft + row.clientWidth >= row.scrollWidth - 1;
      }
      if (prev) prev.addEventListener('click', function () { row.scrollBy({ left: -(row.clientWidth * 0.7), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { row.scrollBy({ left: row.clientWidth * 0.7, behavior: 'smooth' }); });
      row.addEventListener('scroll', throttle(update, 150), { passive: true });
      window.addEventListener('resize', debounce(update, 200));
      update();
    });
  }

  /* ====================================================== HEADING ANIM */
  function initHeadingAnimations(root) {
    if (REDUCED_MOTION) return;
    if (!document.documentElement.classList.contains('studio-animate-headings')) return;
    var isWords = document.documentElement.classList.contains('studio-animate--fade-in-words');
    var isLetters = document.documentElement.classList.contains('studio-animate--fade-in-letters');
    var animTarget = document.documentElement.classList.contains('studio-anim-target--heading') ? 'heading'
      : document.documentElement.classList.contains('studio-anim-target--eyebrow') ? 'eyebrow'
      : 'both';
    var animSel = [];
    if (animTarget !== 'eyebrow') animSel.push('.studio-h1', '.studio-h2', '.studio-h3', '.studio-anim-heading');
    if (animTarget !== 'heading') animSel.push('.studio-eyebrow');
    var targets = $all(animSel.join(', '), root).filter(function (el) {
      return once(el, 'studioAnimBound');
    });
    if (!targets.length) return;

    function split(el, by) {
      var fullText = el.textContent;
      el.setAttribute('aria-label', fullText);
      var segs = [];
      Array.prototype.forEach.call(el.childNodes, function (node) {
        if (node.nodeType === 3) segs.push([node.nodeValue || '', null]);
        else if (node.nodeType === 1) segs.push([node.textContent || '', node.className || null]);
      });
      el.textContent = '';
      function makeSpan(piece, hl) {
        var span = document.createElement('span');
        span.className = (by === 'word' ? 'studio-word' : 'studio-letter') + (hl ? ' ' + hl : '');
        span.setAttribute('aria-hidden', 'true');
        span.style.display = 'inline-block';
        span.textContent = piece;
        return span;
      }
      if (by === 'word') {
        var pendingSpace = false, wroteWord = false;
        segs.forEach(function (seg) {
          seg[0].split(/(\s+)/).forEach(function (tok) {
            if (tok === '') return;
            if (/^\s+$/.test(tok)) { pendingSpace = true; return; }
            if (pendingSpace && wroteWord) el.appendChild(document.createTextNode('\u00a0'));
            pendingSpace = false;
            el.appendChild(makeSpan(tok, seg[1]));
            wroteWord = true;
          });
        });
      } else {
        segs.forEach(function (seg) {
          var t = seg[0], i;
          for (i = 0; i < t.length; i++) {
            el.appendChild(makeSpan(t.charAt(i) === ' ' ? '\u00a0' : t.charAt(i), seg[1]));
          }
        });
      }
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        if (isWords || isLetters) {
          $all(isWords ? '.studio-word' : '.studio-letter', el).forEach(function (n, i) {
            n.style.transitionDelay = (i * (isWords ? 60 : 25)) + 'ms';
            n.classList.add('studio-animated');
          });
        } else {
          el.classList.add('studio-animated');
        }
        io.unobserve(el);
      });
    }, { threshold: 0.15 });

    targets.forEach(function (el) {
      if (isWords) split(el, 'word');
      else if (isLetters) split(el, 'char');
      io.observe(el);
    });
  }

  /* ==================================================== STATS COUNTERS */
  /* Count statistic numbers up from 0 the first time the section enters the
     viewport, then stop observing (animate once, not on every scroll).
     Reduced motion or no IntersectionObserver -> show the final value
     immediately. Non-integer values were rendered static in Liquid (no
     data-stat-value attribute), so they are never selected or touched here. */
  function initStatsCounters(scope) {
    var root = scope || document;
    var sections = $all('[data-stats-media], [data-stats-root]', root);
    if (!sections.length) return;

    function setFinal(nums) {
      nums.forEach(function (n) {
        var t = parseInt(n.getAttribute('data-stat-value'), 10);
        if (!isNaN(t)) n.textContent = String(t);
      });
    }

    function animate(nums) {
      var duration = 1700;
      nums.forEach(function (n) {
        var target = parseInt(n.getAttribute('data-stat-value'), 10);
        if (isNaN(target)) return;
        var startTime = null;
        function step(now) {
          if (startTime === null) startTime = now;
          var p = (now - startTime) / duration;
          if (p >= 1) { n.textContent = String(target); return; } // last frame = exact value
          var eased = 1 - Math.pow(1 - p, 3); // ease-out cubic: fast then settles
          n.textContent = String(Math.round(eased * target));
          requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      });
    }

    sections.forEach(function (section) {
      if (!once(section, 'studioStatsCounted')) return;
      var nums = $all('[data-stat-value]', section);
      if (!nums.length) return;
      if (REDUCED_MOTION || !('IntersectionObserver' in window)) { setFinal(nums); return; }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          animate(nums);
          io.unobserve(entry.target);
        });
      }, { threshold: 0.3 });
      io.observe(section);
    });
  }

  /* ============================================================ TOAST */
  var toastTimer;
  function showToast(message, type) {
    var existing = $('.studio-toast');
    if (existing) existing.remove();
    var toast = document.createElement('div');
    toast.className = 'studio-toast' + (type === 'error' ? ' studio-toast--error' : '');
    toast.setAttribute('role', 'status');
    var icon = document.createElement('span');
    icon.className = 'studio-toast__check';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = type === 'error' ? '\u2715' : '\u2713';
    toast.appendChild(icon);
    toast.appendChild(document.createTextNode(' ' + message));
    document.body.appendChild(toast);
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toast.style.opacity = '0';
      setTimeout(function () { toast.remove(); }, 300);
    }, 2600);
  }

  /* ======================================================= CART DRAWER */
  var cartOpen = false;
  var cartBusy = false;
  var cartTrap = null;

  // Drawer is rendered as a snippet (#studio-cart-root) + a render-only section
  // 'cart-drawer' used purely as the Cart API sections target, so the id is constant.
  function cartSectionId() { return 'cart-drawer'; }
  function cartMount() { return document.getElementById('studio-cart-root'); }
  function cartDrawerEl() { var m = cartMount(); return m ? $('.studio-cart-drawer', m) : null; }
  function cartOverlayEl() { var m = cartMount(); return m ? $('#studio-cart-overlay', m) : null; }

  // carrello-a11y-2110: l'icona del carrello dice anche quanti articoli ci sono ("Carrello, 2 articoli"; negozio solo in italiano)
  function setCartButtonLabel(n) {
    $all('[data-open-cart]').forEach(function (b) {
      if (!b.dataset.baseLabel) b.dataset.baseLabel = b.getAttribute('aria-label') || '';
      b.setAttribute('aria-label', b.dataset.baseLabel + (n > 0 ? ', ' + n + (n === 1 ? ' articolo' : ' articoli') : ''));
    });
  }

  function setCartCount(count) {
    setCartButtonLabel(parseInt(count, 10) || 0);
    var el = document.getElementById('studio-cart-count');
    if (!el) return;
    var n = parseInt(count, 10) || 0;
    el.textContent = n > 0 ? n : '';
    el.style.display = n > 0 ? '' : 'none';
  }

  // carrello-a11y-2110: a carrello aperto la pagina dietro esce da tastiera e lettore di schermo (inert): header, main e footer.
  // Restano fuori la regione degli annunci, i popup, WhatsApp e la barra cookie.
  var cartInerted = [];
  function setBackgroundInert(on) {
    if (on) {
      cartInerted = $all('body > .skip-to-content-link, body > .shopify-section-group-header-group, body > main, body > .shopify-section-group-footer-group')
        .filter(function (el) { return !el.hasAttribute('inert'); });
      cartInerted.forEach(function (el) { el.setAttribute('inert', ''); });
    } else {
      cartInerted.forEach(function (el) { el.removeAttribute('inert'); });
      cartInerted = [];
    }
  }

  function openCart() {
    var drawer = cartDrawerEl(), overlay = cartOverlayEl();
    if (!drawer) return;
    // carrello-a11y-2110: già aperto (aggiunta da "Potrebbe piacerti anche"): niente secondo blocco dello scorrimento né secondo
    // recinto del fuoco (prima, chiuso il carrello, la pagina restava bloccata e il Tab fermo)
    if (cartOpen) { if (cartTrap) cartTrap.container = drawer; return; }
    // alla chiusura il fuoco torna su chi l'ha aperto (es. "Aggiungi al carrello"), altrimenti sull'icona del carrello
    var opener = document.activeElement;
    // (solo pulsanti e link: Safari non dà il fuoco al pulsante toccato e prende il contenitore, es. <main tabindex="-1">)
    if (!opener || drawer.contains(opener) || !opener.matches('a[href], button, input, select, textarea, summary, [role="button"]')) opener = document.querySelector('[data-open-cart]');
    cartOpen = true;
    drawer.classList.add('studio-cart-drawer--open');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.removeAttribute('inert');
    if (overlay) overlay.classList.add('studio-overlay--visible');
    setBackgroundInert(true);
    lockScroll();
    cartTrap = new FocusTrap(drawer);
    cartTrap.activate(opener);
  }

  function closeCart() {
    if (!cartOpen) return; // carrello-a11y-2110: mai togliere un blocco dello scorrimento che non è del carrello
    var drawer = cartDrawerEl(), overlay = cartOverlayEl();
    cartOpen = false;
    if (drawer) {
      drawer.classList.remove('studio-cart-drawer--open');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.setAttribute('inert', '');
    }
    if (overlay) overlay.classList.remove('studio-overlay--visible');
    setBackgroundInert(false); // prima di rimettere il fuoco: un elemento inert non lo riceve
    unlockScroll();
    if (cartTrap) { cartTrap.deactivate(); cartTrap = null; }
  }

  // carrello-a11y-2110: dopo il ridisegno il fuoco torna sullo stesso controllo della stessa riga, o sul titolo del carrello se la
  // riga non c'è più (cestino, quantità a 0) o se si è aggiunto un suggerimento; prima finiva sul body (lettore di schermo
  // da capo). La riga si ritrova per posizione: Shopify cambia la chiave della riga quando cambia lo sconto applicato.
  // Il controllo toccato si segna al clic (Safari non dà il fuoco ai pulsanti toccati).
  var cartFocusHint = null;
  function cartRows(drawer) { return drawer ? $all('.studio-cart-item', drawer) : []; }
  function cartFocusKey(el) {
    if (!el || !el.closest) return null;
    var line = el.closest('.studio-cart-item');
    var kind = el.matches('[data-action="plus"]') ? 'plus' : el.matches('[data-action="minus"]') ? 'minus'
      : el.matches('[data-cart-remove]') ? 'remove' : el.matches('.studio-cart-item__name') ? 'name' : null;
    var rows = cartRows(cartDrawerEl());
    return line && kind ? { index: rows.indexOf(line), rows: rows.length, kind: kind } : { title: true };
  }
  function restoreCartFocus(drawer, key) {
    if (!drawer || !key) return;
    var target = null, rows = cartRows(drawer);
    if (!key.title && rows.length === key.rows && rows[key.index]) {
      var sel = { plus: '[data-action="plus"]', minus: '[data-action="minus"]', remove: '[data-cart-remove]', name: '.studio-cart-item__name' }[key.kind];
      target = rows[key.index].querySelector(sel);
    }
    target = target || drawer.querySelector('.studio-cart-drawer__title');
    if (target) target.focus({ preventScroll: true });
  }

  // Replace the rendered cart section's inner markup, preserving open state.
  function renderCartSection(html) {
    var mount = cartMount();
    if (!mount) return;
    var hint = cartFocusHint || (mount.contains(document.activeElement) ? cartFocusKey(document.activeElement) : null);
    cartFocusHint = null;
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    var fresh = tmp.querySelector('#shopify-section-' + cartSectionId()) || tmp.firstElementChild;
    mount.innerHTML = fresh ? fresh.innerHTML : html;

    var root = mount.querySelector('[data-cart-count]');
    if (root) setCartCount(root.getAttribute('data-cart-count'));

    if (cartOpen) {
      var drawer = cartDrawerEl(), overlay = cartOverlayEl();
      if (drawer) { drawer.classList.add('studio-cart-drawer--open'); drawer.setAttribute('aria-hidden', 'false'); drawer.removeAttribute('inert'); }
      if (overlay) overlay.classList.add('studio-overlay--visible');
      if (cartTrap && drawer) cartTrap.container = drawer;
      restoreCartFocus(drawer, hint);
    }
    loadCartRecommendations(mount);
  }

  // carrello-a11y-2110: annuncio dopo una modifica: cosa è cambiato e il nuovo totale, letti dal carrello ridisegnato
  function cartUpdateMessage(before, quantity) {
    var d = cartDrawerEl();
    var parts = [STRINGS.cartUpdated || ''];
    var count = d ? parseInt(d.getAttribute('data-cart-count'), 10) || 0 : 0;
    if (!count) { parts.push('Il carrello è vuoto'); return parts.filter(Boolean).join('. '); }
    var rows = cartRows(d);
    var row = quantity > 0 && before && rows.length === before.rows ? rows[before.index] : null;
    var q = row && row.querySelector('.studio-qty__value');
    parts.push(row ? 'Quantità ' + (q ? q.textContent.trim() : quantity) : 'Prodotto rimosso');
    var spans = d ? $all('.studio-cart-total span', d) : [];
    if (spans.length >= 2) parts.push(spans[0].textContent.trim() + ' ' + spans[spans.length - 1].textContent.trim());
    return parts.filter(Boolean).join('. ');
  }

  function withSections(body) {
    return Object.assign({}, body, {
      sections: cartSectionId(),
      sections_url: window.location.pathname
    });
  }

  async function cartFetch(url, body) {
    var res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(withSections(body))
    });
    var data = await res.json();
    if (!res.ok) throw data;
    var sid = cartSectionId();
    if (data.sections && data.sections[sid]) renderCartSection(data.sections[sid]);
    return data;
  }

  async function addToCart(id, quantity, button, extra) {
    if (!id) return;
    if (button) { button.classList.add('is-loading'); button.setAttribute('aria-busy', 'true'); button.disabled = true; }
    try {
      var payload = { id: id, quantity: quantity || 1 };
      if (extra && typeof extra === 'object') {
        if (extra.selling_plan) payload.selling_plan = extra.selling_plan;
        if (extra.properties && Object.keys(extra.properties).length) payload.properties = extra.properties;
      }
      var item = await cartFetch(ROUTES.cartAdd || '/cart/add.js', payload);
      announce(STRINGS.addedToCart || '');
      if (CART.flow === 'page') { window.location.href = ROUTES.cart || '/cart'; return; }
      if (CART.flow === 'popup') { showToast((STRINGS.addedToCart || '') + (item && item.title ? ' — ' + item.title : '')); return; }
      openCart();
    } catch (err) {
      // gift-card-0110: description può essere un oggetto (errori per campo del destinatario gift card): mai "[object Object]".
      // L'errore torna al chiamante, che può mostrarlo vicino ai campi (sections/product.liquid).
      var desc = err && err.description;
      var msg = (typeof desc === 'string' && desc) || STRINGS.cartError || (err && err.message) || 'Error';
      showToast(msg, 'error');
      return { error: err };
    } finally {
      if (button) { button.classList.remove('is-loading'); button.removeAttribute('aria-busy'); button.disabled = false; }
    }
  }
  S.addToCart = addToCart; // consumed by the product section

  // Multi-item add (bundle): /cart/add.js accetta {items:[...]}. Riusa cartFetch (stesso re-render del cart-drawer + apertura).
  // ATOMICO: se un item e' sold-out fallisce l'intera chiamata -> la sezione bundle disabilita i sold-out a monte.
  async function addItemsToCart(items, button) {
    if (!items || !items.length) return;
    if (button) { button.classList.add('is-loading'); button.setAttribute('aria-busy', 'true'); button.disabled = true; }
    try {
      await cartFetch(ROUTES.cartAdd || '/cart/add.js', { items: items });
      announce(STRINGS.addedToCart || '');
      if (CART.flow === 'page') { window.location.href = ROUTES.cart || '/cart'; return; }
      if (CART.flow === 'popup') { showToast(STRINGS.addedToCart || ''); return; }
      openCart();
    } catch (err) {
      var msg = (err && (err.description || err.message)) || STRINGS.cartError || 'Error';
      showToast(msg, 'error');
    } finally {
      if (button) { button.classList.remove('is-loading'); button.removeAttribute('aria-busy'); button.disabled = false; }
    }
  }
  S.addItemsToCart = addItemsToCart; // consumed by the bundle section

  async function changeQty(line, quantity) {
    if (cartBusy) { cartFocusHint = null; return; }
    cartBusy = true;
    var drawer = cartDrawerEl();
    if (drawer) drawer.setAttribute('aria-busy', 'true');
    var rowsBefore = cartRows(drawer), before = null; // carrello-a11y-2110: posizione della riga per l'annuncio
    rowsBefore.forEach(function (r, i) { if (r.getAttribute('data-line') === line) before = { index: i, rows: rowsBefore.length }; });
    try {
      await cartFetch(ROUTES.cartChange || '/cart/change.js', { id: line, quantity: quantity });
      announce(cartUpdateMessage(before, quantity)); // carrello-a11y-2110
    } catch (err) {
      cartFocusHint = null;
      showToast((err && (err.description || err.message)) || STRINGS.cartError || 'Error', 'error');
    } finally {
      cartBusy = false;
      var d = cartDrawerEl();
      if (d) d.removeAttribute('aria-busy');
    }
  }

  var saveNote = debounce(function (value) {
    fetch(ROUTES.cartUpdate || '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: value })
    });
  }, 600);

  // pagine2-0210: "Ti potrebbe piacere" del cassetto con il selettore taglia del tema. Le taglie esaurite si barrano per
  // il colore mostrato (data-rec-color); scelta la taglia, "Aggiungi" prende la variante colore + taglia.
  function recVariants(li) {
    if (li._recVariants) return li._recVariants;
    var s = li.querySelector('[data-rec-variants]');
    try { li._recVariants = s ? JSON.parse(s.textContent) : []; } catch (e) { li._recVariants = []; }
    return li._recVariants;
  }
  function recMatches(li, size) {
    var si = parseInt(li.getAttribute('data-rec-size-index'), 10);
    var ci = parseInt(li.getAttribute('data-rec-color-index'), 10);
    var color = li.getAttribute('data-rec-color');
    return recVariants(li).filter(function (v) { return v.o[si] === size && (!(ci >= 0) || v.o[ci] === color); });
  }
  function recMissing(li, on) {
    var opt = li.querySelector('.studio-product__option');
    var msg = li.querySelector('[data-rec-msg]');
    if (opt) opt.classList.toggle('is-missing', on);
    if (msg) msg.hidden = !on;
  }
  function initRecSizes(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-cart-rec][data-rec-size-index]'), function (li) {
      Array.prototype.forEach.call(li.querySelectorAll('input.studio-product__size-input'), function (input) {
        var ok = recMatches(li, input.value).some(function (v) { return v.a; });
        input.disabled = !ok;
        var span = input.nextElementSibling;
        if (span) span.classList.toggle('studio-product__size--unavailable', !ok);
      });
    });
    if (document.body.dataset.recSizesBound) return;
    document.body.dataset.recSizesBound = '1';
    document.addEventListener('change', function (e) {
      var input = e.target;
      if (!input.matches || !input.matches('[data-cart-rec] input.studio-product__size-input')) return;
      var li = input.closest('[data-cart-rec]');
      var v = recMatches(li, input.value).filter(function (x) { return x.a; })[0];
      var btn = li.querySelector('[data-rec-add]');
      if (btn) btn.dataset.variantId = v ? String(v.id) : '';
      recMissing(li, false);
    });
  }

  // Lazy-load cart recommendations via the Recommendations Section Rendering API.
  function loadCartRecommendations(scope) {
    var holder = (scope || document).querySelector('[data-cart-recs][data-product-id]');
    if (!holder || holder.dataset.loaded || !ROUTES.recommendations) return;
    var pid = holder.dataset.productId;
    if (!pid) return;
    holder.dataset.loaded = 'true';
    var url = ROUTES.recommendations + '?section_id=' + CART.recsSection + '&product_id=' + encodeURIComponent(pid) + '&limit=4';
    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var tmp = document.createElement('div');
        tmp.innerHTML = text;
        var list = tmp.querySelector('.studio-cart-recs__list');
        if (list && list.children.length) { holder.appendChild(list); initRecSizes(holder); }
        else holder.remove();
      })
      .catch(function () { holder.remove(); });
  }

  // PDP recommendations — same Recommendations Section Rendering API, lazy-loaded
  // when the placeholder nears the viewport. Cards are document-delegated, so
  // swatches / quick view / quick add work on the injected markup automatically.
  function loadProductRecommendations(holder) {
    if (!holder || holder.dataset.loaded || !ROUTES.recommendations) return;
    var pid = holder.dataset.productId;
    if (!pid) return;
    holder.dataset.loaded = 'true';
    var limit = holder.dataset.limit || '4';
    var intent = holder.dataset.intent || 'related';
    var url = ROUTES.recommendations + '?section_id=product-recommendations&product_id=' + encodeURIComponent(pid) + '&limit=' + encodeURIComponent(limit) + '&intent=' + encodeURIComponent(intent);
    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var tmp = document.createElement('div');
        tmp.innerHTML = text;
        // The API branch now returns only the cards. card_class puts
        // studio-recs__card on each card root, so this selects each card once.
        var cards = tmp.querySelectorAll('.studio-recs__card, .studio-card');
        if (cards.length) {
          cards.forEach(function (card) { holder.appendChild(card); });
        } else {
          // No recommendations: remove the whole recs section.
          var sec = holder.closest('.studio-recs');
          if (sec) { sec.remove(); } else { holder.remove(); }
        }
      })
      .catch(function () {
        var sec = holder.closest('.studio-recs');
        if (sec) { sec.remove(); } else { holder.remove(); }
      });
  }

  function initProductRecommendations(scope) {
    var holders = (scope || document).querySelectorAll('[data-product-recs][data-product-id]');
    holders.forEach(function (holder) {
      if (holder.dataset.bound) return;
      holder.dataset.bound = 'true';
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries, obs) {
          entries.forEach(function (en) {
            if (en.isIntersecting) { obs.disconnect(); loadProductRecommendations(holder); }
          });
        }, { rootMargin: '400px 0px' });
        io.observe(holder);
      } else {
        loadProductRecommendations(holder);
      }
    });
  }

  // Cart events are delegated on document so re-rendered markup keeps working.
  function initCartDelegation() {
    if (!once(document.body, 'studioCartBound')) return;
    var cartRoot = document.querySelector('#studio-cart-root [data-cart-count]'); // carrello-a11y-2110
    if (cartRoot) setCartButtonLabel(parseInt(cartRoot.getAttribute('data-cart-count'), 10) || 0);

    document.addEventListener('click', function (e) {
      var openBtn = e.target.closest('[data-open-cart]');
      if (openBtn) { e.preventDefault(); openCart(); return; }

      var recAdd = e.target.closest('[data-rec-add]');
      if (recAdd) {
        e.preventDefault();
        if (recAdd.dataset.variantId) { if (cartOpen) cartFocusHint = { title: true }; addToCart(recAdd.dataset.variantId, 1, recAdd); return; } // carrello-a11y-2110
        var recLi = recAdd.closest('[data-cart-rec]');
        if (recLi) recMissing(recLi, true); // pagine2-0210: senza taglia non aggiunge
        return;
      }

      // Collection-card quick add. The PDP submit button has no data-variant-id,
      // so this targets only standalone quick-add buttons (no double-binding).
      var quickAdd = e.target.closest('[data-add-to-cart][data-variant-id]');
      if (quickAdd) {
        e.preventDefault();
        addToCart(quickAdd.dataset.variantId, 1, quickAdd);
        return;
      }

      if (e.target.closest('[data-cart-close]') || e.target.closest('#studio-cart-overlay')) {
        e.preventDefault(); closeCart(); return;
      }

      var qtyBtn = e.target.closest('[data-cart-qty]');
      if (qtyBtn) {
        var item = qtyBtn.closest('.studio-cart-item');
        if (!item) return;
        var valueEl = item.querySelector('.studio-qty__value');
        var current = parseInt(valueEl ? valueEl.textContent : '0', 10) || 0;
        var next = qtyBtn.dataset.action === 'plus' ? current + 1 : Math.max(0, current - 1);
        cartFocusHint = cartFocusKey(qtyBtn); // carrello-a11y-2110
        changeQty(item.dataset.line, next);
        return;
      }

      var removeBtn = e.target.closest('[data-cart-remove]');
      if (removeBtn) {
        e.preventDefault();
        var row = removeBtn.closest('.studio-cart-item');
        if (row) { cartFocusHint = cartFocusKey(removeBtn); changeQty(row.dataset.line, 0); } // carrello-a11y-2110
        return;
      }
    });

    document.addEventListener('input', function (e) {
      if (e.target.matches('[data-cart-note]')) saveNote(e.target.value);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && cartOpen) closeCart();
    });
  }

  /* ===================================================== PAGE TRANSITION */
  function initPageTransition() {
    if (REDUCED_MOTION) return;
    if (!document.documentElement.classList.contains('studio-transitions')) return;
    if (window.Shopify && window.Shopify.designMode) return;

    document.addEventListener('click', function (e) {
      // Respect new-tab / modified clicks and non-primary buttons.
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var link = e.target.closest('a');
      if (!link) return;
      var href = link.getAttribute('href');
      if (!href || href.charAt(0) === '#') return;
      if (link.target && link.target !== '_self') return;
      if (link.hasAttribute('download') || link.dataset.noTransition !== undefined) return;
      if (/^(mailto:|tel:|javascript:)/i.test(href)) return;
      var url;
      try { url = new URL(href, window.location.href); } catch (err) { return; }
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;

      e.preventDefault();
      document.documentElement.classList.add('studio-leaving');
      setTimeout(function () { window.location.href = url.href; }, 220);
    });

    window.addEventListener('pageshow', function (e) {
      if (e.persisted) document.documentElement.classList.remove('studio-leaving');
    });
  }

  /* ================================================== COLLECTION FACETS */
  function facetsContainer() { return $('[data-facets-container]'); }

  var facetTrap = null;

  function facetDrawerOpen() {
    var c = facetsContainer();
    return !!(c && c.classList.contains('studio-facets--drawer-open'));
  }
  function openFacetDrawer() {
    var c = facetsContainer();
    if (!c) return;
    c.classList.add('studio-facets--drawer-open');
    var overlay = c.querySelector('.studio-facets__overlay');
    if (overlay) overlay.hidden = false;
    var trigger = c.querySelector('[data-facet-open]');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    lockScroll();
    var panel = c.querySelector('.studio-facets-panel');
    if (panel) { facetTrap = new FocusTrap(panel); facetTrap.activate(trigger); }
  }
  function closeFacetDrawer() {
    var c = facetsContainer();
    if (!c) return;
    c.classList.remove('studio-facets--drawer-open');
    var overlay = c.querySelector('.studio-facets__overlay');
    if (overlay) overlay.hidden = true;
    var trigger = c.querySelector('[data-facet-open]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (facetTrap) { facetTrap.deactivate(); facetTrap = null; }
    unlockScroll();
  }

  // Serialise the filter form (checked boxes, non-empty prices, sort) into a URL.
  // The sort <select> lives in the toolbar but is associated via form=, so
  // FormData(form) picks it up automatically.
  function urlFromFacetForm(form) {
    var params = new URLSearchParams();
    var fd = new FormData(form);
    fd.forEach(function (value, key) {
      if (value !== '' && value != null) params.append(key, value);
    });
    var action = (form.getAttribute('action') || window.location.pathname).split('?')[0];
    var qs = params.toString();
    return action + (qs ? '?' + qs : '');
  }

  // Fetch the collection section for a given URL and swap the results in place.
  function renderFacets(url, opts) {
    opts = opts || {};
    var c = facetsContainer();
    if (!c) return;
    var sectionId = c.getAttribute('data-section-id');
    var u;
    try { u = new URL(url, window.location.origin); } catch (err) { return; }

    if (!opts.noPush) {
      window.history.pushState({ studioFacets: true }, '', u.pathname + u.search);
    }

    var sep = u.search ? '&' : '?';
    var fetchUrl = u.pathname + u.search + sep + 'section_id=' + encodeURIComponent(sectionId);

    c.classList.add('studio-facets--loading');
    c.setAttribute('aria-busy', 'true');

    fetch(fetchUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { if (!r.ok) throw new Error('bad'); return r.text(); })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var fresh = doc.querySelector('[data-facets-container]');
        if (!fresh) { window.location.href = u.href; return; }
        var wasOpen = facetDrawerOpen();
        if (facetTrap) { facetTrap.deactivate(); facetTrap = null; }
        c.innerHTML = fresh.innerHTML;
        if (wasOpen) {
          var overlay = c.querySelector('.studio-facets__overlay');
          if (overlay) overlay.hidden = false;
          var panel = c.querySelector('.studio-facets-panel');
          if (panel) { facetTrap = new FocusTrap(panel); facetTrap.activate(); }
        }
        announce(c.getAttribute('data-results-text') || '');
      })
      .catch(function () { window.location.href = u.href; })
      .finally(function () {
        c.classList.remove('studio-facets--loading');
        c.removeAttribute('aria-busy');
      });
  }

  var applyFacetForm = debounce(function (form) { renderFacets(urlFromFacetForm(form)); }, 350);

  function initFacets() {
    if (!once(document.body, 'studioFacetsBound')) return;

    // Filter input / sort change -> apply. e.target.form respects the form=
    // attribute, so the toolbar sort <select> is covered when filters exist.
    document.addEventListener('change', function (e) {
      var form = e.target.form;
      if (form && form.matches && form.matches('[data-facet-form]')) { applyFacetForm(form); return; }
      // Sorting with filters disabled: no facet form exists -> sort via URL.
      if (e.target.matches('[data-facet-sort]')) {
        var u = new URL(window.location.href);
        if (e.target.value) { u.searchParams.set('sort_by', e.target.value); }
        else { u.searchParams.delete('sort_by'); }
        u.searchParams.delete('page');
        renderFacets(u.pathname + u.search);
      }
    });

    // Enter / no-JS submit.
    document.addEventListener('submit', function (e) {
      var form = e.target.closest ? e.target.closest('[data-facet-form]') : null;
      if (!form) return;
      e.preventDefault();
      renderFacets(urlFromFacetForm(form));
    });

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-facet-open]')) { e.preventDefault(); openFacetDrawer(); return; }
      if (e.target.closest('[data-facet-close]')) { e.preventDefault(); closeFacetDrawer(); return; }
      var remove = e.target.closest('[data-facet-remove]');
      if (remove && remove.getAttribute('href')) { e.preventDefault(); renderFacets(remove.getAttribute('href')); return; }
      var page = e.target.closest('[data-facet-page]');
      if (page && page.getAttribute('href')) {
        e.preventDefault();
        renderFacets(page.getAttribute('href'));
        var c = facetsContainer();
        if (c) c.scrollIntoView({ behavior: REDUCED_MOTION ? 'auto' : 'smooth', block: 'start' });
        return;
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && facetDrawerOpen()) closeFacetDrawer();
    });

    window.addEventListener('popstate', function () {
      if (facetsContainer()) renderFacets(window.location.pathname + window.location.search, { noPush: true });
    });
  }

  /* ============================================== PREDICTIVE SEARCH */
  var searchOpen = false;
  var searchActiveIndex = -1;

  function searchPanelEl() { return $('[data-search-panel]'); }
  function searchInputEl() { return $('[data-search-input]'); }
  function searchResultsEl() { return $('[data-search-results]'); }
  function searchOpenerEl() { return $('[data-open-search]'); }

  function openSearch() {
    var panel = searchPanelEl();
    if (!panel || searchOpen) return;
    searchOpen = true;
    panel.classList.add('studio-search-panel--open');
    panel.setAttribute('aria-hidden', 'false');
    var opener = searchOpenerEl();
    if (opener) opener.setAttribute('aria-expanded', 'true');
    var input = searchInputEl();
    if (input) window.setTimeout(function () { input.focus(); }, 60);
  }
  function closeSearch() {
    var panel = searchPanelEl();
    if (!panel || !searchOpen) return;
    searchOpen = false;
    panel.classList.remove('studio-search-panel--open');
    panel.setAttribute('aria-hidden', 'true');
    var opener = searchOpenerEl();
    if (opener) { opener.setAttribute('aria-expanded', 'false'); opener.focus(); }
    clearSearchActive();
  }

  function searchOptionEls() {
    var r = searchResultsEl();
    return r ? $all('[role="option"]', r) : [];
  }
  function clearSearchActive() {
    searchActiveIndex = -1;
    searchOptionEls().forEach(function (o) { o.classList.remove('is-active'); o.removeAttribute('aria-selected'); });
    var input = searchInputEl();
    if (input) input.removeAttribute('aria-activedescendant');
  }
  function setSearchActive(i) {
    var opts = searchOptionEls();
    if (!opts.length) return;
    if (i < 0) i = opts.length - 1;
    if (i >= opts.length) i = 0;
    searchActiveIndex = i;
    opts.forEach(function (o, idx) {
      var on = idx === i;
      o.classList.toggle('is-active', on);
      if (on) { o.setAttribute('aria-selected', 'true'); o.scrollIntoView({ block: 'nearest' }); }
      else o.removeAttribute('aria-selected');
    });
    var input = searchInputEl();
    if (input) input.setAttribute('aria-activedescendant', opts[i].id);
  }

  var runPredictiveSearch = debounce(function () {
    var input = searchInputEl();
    var results = searchResultsEl();
    if (!input || !results) return;
    var q = input.value.trim();
    if (q.length < 2) {
      results.innerHTML = '';
      clearSearchActive();
      input.setAttribute('aria-expanded', 'false');
      return;
    }
    if (!ROUTES.predictiveSearch) return;
    var url = ROUTES.predictiveSearch +
      '?q=' + encodeURIComponent(q) +
      '&section_id=predictive-search' +
      '&resources[type]=product,collection,page,article' +
      '&resources[limit]=6' +
      '&resources[options][unavailable_products]=last';
    results.classList.add('studio-predictive--loading');
    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var fresh = doc.querySelector('[data-predictive]');
        results.innerHTML = fresh ? fresh.outerHTML : '';
        var opts = searchOptionEls();
        opts.forEach(function (o, i) { o.id = 'studio-predictive-opt-' + i; });
        clearSearchActive();
        input.setAttribute('aria-expanded', opts.length ? 'true' : 'false');
        var holder = results.querySelector('[data-predictive]');
        if (holder) announce(holder.getAttribute('data-results-text') || '');
      })
      .catch(function () { /* keep previous results on transient error */ })
      .finally(function () { results.classList.remove('studio-predictive--loading'); });
  }, 250);

  function initPredictiveSearch() {
    if (!once(document.body, 'studioSearchBound')) return;

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-open-search]')) {
        e.preventDefault();
        if (searchOpen) { closeSearch(); } else { openSearch(); }
        return;
      }
      if (e.target.closest('[data-close-search]')) { e.preventDefault(); closeSearch(); return; }
      // A click outside the open panel closes it.
      if (searchOpen) {
        var panel = searchPanelEl();
        if (panel && !panel.contains(e.target)) closeSearch();
      }
    });

    document.addEventListener('input', function (e) {
      if (e.target.matches('[data-search-input]')) runPredictiveSearch();
    });

    document.addEventListener('keydown', function (e) {
      if (!searchOpen) return;
      if (e.key === 'Escape') { e.preventDefault(); closeSearch(); return; }
      if (!e.target.matches('[data-search-input]')) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setSearchActive(searchActiveIndex + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setSearchActive(searchActiveIndex - 1); }
      else if (e.key === 'Enter') {
        var opts = searchOptionEls();
        if (searchActiveIndex >= 0 && opts[searchActiveIndex]) {
          e.preventDefault();
          var href = opts[searchActiveIndex].getAttribute('href');
          if (href) window.location.href = href;
        }
        // otherwise the form submits natively to the full search results page
      }
    });
  }

  /* ============================================== CARD SWATCHES */
  // Hovering / focusing / tapping a card swatch previews that variant's image.
  function previewCardSwatch(sw) {
    var media = sw.getAttribute('data-swatch-media');
    if (!media) return;
    var card = sw.closest('.studio-card');
    if (!card) return;
    var img = card.querySelector('.studio-card__img--primary');
    if (!img) return;
    if (!img.dataset.defaultSrc) img.dataset.defaultSrc = img.getAttribute('src') || '';
    img.src = media;
    card.classList.add('studio-card--swatch-active');
    $all('[data-card-swatch]', card).forEach(function (s) { s.classList.toggle('is-active', s === sw); });
  }
  function revertCardSwatch(card) {
    if (!card) return;
    var img = card.querySelector('.studio-card__img--primary');
    if (img && img.dataset.defaultSrc) img.src = img.dataset.defaultSrc;
    card.classList.remove('studio-card--swatch-active');
    $all('[data-card-swatch]', card).forEach(function (s) { s.classList.remove('is-active'); });
  }
  function initCardSwatches() {
    if (!once(document.body, 'studioCardSwatchBound')) return;
    document.addEventListener('mouseover', function (e) {
      var sw = e.target.closest('[data-card-swatch]');
      if (sw) previewCardSwatch(sw);
    });
    document.addEventListener('focusin', function (e) {
      var sw = e.target.closest('[data-card-swatch]');
      if (sw) previewCardSwatch(sw);
    });
    document.addEventListener('mouseout', function (e) {
      var card = e.target.closest('.studio-card');
      if (card && !card.contains(e.relatedTarget)) revertCardSwatch(card);
    });
    document.addEventListener('focusout', function (e) {
      var card = e.target.closest('.studio-card');
      if (card && !card.contains(e.relatedTarget)) revertCardSwatch(card);
    });
    document.addEventListener('click', function (e) {
      var sw = e.target.closest('[data-card-swatch]');
      if (sw) { e.preventDefault(); previewCardSwatch(sw); }
    });
  }

  /* ============================================== QUICK VIEW + STICKY ATC */
  // Resolve the selected variant from a scope containing a [data-qv-variants]
  // JSON blob and [data-option-index] inputs. Used by the quick-view modal.
  function studioResolveVariant(root) {
    var dataEl = root.querySelector('[data-qv-variants]');
    if (!dataEl) return null;
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return null; }
    var inputs = $all('[data-option-index]', root);
    var opts = {};
    inputs.forEach(function (i) {
      if (i.checked || i.tagName === 'SELECT') opts[i.dataset.optionIndex] = i.value;
    });
    var sel = Object.keys(opts).sort(function (a, b) { return a - b; }).map(function (k) { return opts[k]; });
    return (data.variants || []).find(function (v) {
      return v.options.length === sel.length && sel.every(function (val, i) { return v.options[i] === val; });
    }) || null;
  }

  function updateQuickViewVariant(root) {
    var v = studioResolveVariant(root);
    if (!v) return;
    var price = root.querySelector('[data-qv-price]');
    if (price) price.innerHTML = v.price + (v.compareAt ? ' <s>' + v.compareAt + '</s>' : '');
    var atc = root.querySelector('[data-qv-atc]');
    if (atc) {
      atc.dataset.variantId = v.id;
      atc.disabled = !v.available;
      atc.textContent = v.available ? atc.getAttribute('data-add-text') : atc.getAttribute('data-sold-out-text');
    }
    if (v.image) {
      var img = root.querySelector('[data-qv-image]');
      if (img) img.src = v.image;
    }
  }

  var qvTrap = null;
  var qvOpener = null;

  // Loads Shopify's model-viewer component + Shopify XR once, on demand.
  // The quick-view can inject a <model-viewer> from a collection/search/home
  // page where these libraries aren't otherwise present. Idempotent (guarded
  // flag). Exposed on window.studio so other surfaces can reuse it.
  var modelViewerLibRequested = false;
  function ensureModelViewerLib() {
    if (modelViewerLibRequested) return;
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;
    modelViewerLibRequested = true;
    window.Shopify.loadFeatures([
      { name: 'model-viewer-ui', version: '1.0', onLoad: function () {} },
      { name: 'shopify-xr', version: '1.0', onLoad: function () {} }
    ]);
  }
  S.ensureModelViewerLib = ensureModelViewerLib;

  function ensureQuickViewShell() {
    var modal = $('[data-qv-modal]');
    if (modal) return modal;
    modal = document.createElement('div');
    modal.className = 'studio-qv-modal';
    modal.setAttribute('data-qv-modal', '');
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML =
      '<div class="studio-qv-modal__overlay" data-qv-close></div>' +
      '<div class="studio-qv-modal__panel" role="dialog" aria-modal="true" aria-label="' + (STRINGS.quickView || 'Quick view') + '">' +
        '<button type="button" class="studio-qv-modal__close" data-qv-close aria-label="' + (STRINGS.close || 'Close') + '">' +
          '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
        '</button>' +
        '<div class="studio-qv-modal__body" data-qv-body></div>' +
      '</div>';
    document.body.appendChild(modal);
    return modal;
  }

  function openQuickView(url, opener) {
    if (!url) return;
    var modal = ensureQuickViewShell();
    var body = modal.querySelector('[data-qv-body]');
    qvOpener = opener || document.activeElement;
    body.innerHTML = '<div class="studio-qv-modal__loading">' + (STRINGS.loading || '') + '</div>';
    modal.classList.add('studio-qv-modal--open');
    modal.setAttribute('aria-hidden', 'false');
    lockScroll();
    var fetchUrl = url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=quick-view';
    fetch(fetchUrl)
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var content = doc.querySelector('[data-qv]');
        body.innerHTML = content ? content.outerHTML : '<div class="studio-qv-modal__loading">' + (STRINGS.cartError || '') + '</div>';
        var root = body.querySelector('[data-qv]');
        if (root) updateQuickViewVariant(root);
        if (body.querySelector('model-viewer')) ensureModelViewerLib();
        if (qvTrap) qvTrap.deactivate();
        qvTrap = new FocusTrap(modal.querySelector('.studio-qv-modal__panel'));
        qvTrap.activate(qvOpener);
      })
      .catch(function () {
        body.innerHTML = '<div class="studio-qv-modal__loading">' + (STRINGS.cartError || '') + '</div>';
      });
  }

  function closeQuickView() {
    var modal = $('[data-qv-modal]');
    if (!modal || !modal.classList.contains('studio-qv-modal--open')) return;
    modal.querySelectorAll('video').forEach(function (v) { try { v.pause(); } catch (e) {} });
    modal.querySelectorAll('iframe').forEach(function (f) { f.src = ''; });
    modal.classList.remove('studio-qv-modal--open');
    modal.setAttribute('aria-hidden', 'true');
    if (qvTrap) { qvTrap.deactivate(); qvTrap = null; }
    unlockScroll();
  }

  function initQuickView() {
    if (!once(document.body, 'studioQuickViewBound')) return;
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-quick-view][data-product-url]');
      if (trigger) { e.preventDefault(); openQuickView(trigger.getAttribute('data-product-url'), trigger); return; }
      if (e.target.closest('[data-qv-close]')) { e.preventDefault(); closeQuickView(); return; }
      var atc = e.target.closest('[data-qv-atc]');
      if (atc && atc.dataset.variantId && !atc.disabled) {
        e.preventDefault();
        addToCart(atc.dataset.variantId, 1, atc);
        closeQuickView();
        return;
      }
    });
    document.addEventListener('change', function (e) {
      var root = e.target.closest('[data-qv]');
      if (root && e.target.matches('[data-option-index]')) updateQuickViewVariant(root);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var modal = $('[data-qv-modal]');
      if (modal && modal.classList.contains('studio-qv-modal--open')) closeQuickView();
    });
  }

  // Sticky add-to-cart (mobile). Mirrors the PDP form's price/availability and
  // adds the current variant. Fully decoupled — the inline PDP handler updates
  // the DOM at the target phase, so this document-bubble sync runs after it.
  function initStickyAtc() {
    var bar = $('[data-sticky-atc]');
    if (!bar || !once(bar, 'studioStickyBound')) return;
    var form = $('[data-product-form]');
    if (!form) return;
    var mainAtc = form.querySelector('[data-add-to-cart]');
    var variantInput = form.querySelector('[data-variant-id]');
    var stickyBtn = bar.querySelector('[data-sticky-atc-button]');
    var stickyPrice = bar.querySelector('[data-sticky-price]');

    function sync() {
      var pc = $('.studio-product__price-current');
      if (pc && stickyPrice) stickyPrice.textContent = pc.textContent.trim();
      if (mainAtc && stickyBtn) {
        stickyBtn.disabled = mainAtc.disabled;
        // pdp-0210: la barra può avere testi suoi (più corti)
        var addT = stickyBtn.getAttribute('data-add-text') || mainAtc.getAttribute('data-add-text');
        var outT = stickyBtn.getAttribute('data-sold-out-text') || mainAtc.getAttribute('data-sold-out-text');
        if (addT && outT) stickyBtn.textContent = mainAtc.disabled ? outT : addT;
      }
    }

    document.addEventListener('change', function (e) {
      if (e.target.closest('[data-product-form]') && e.target.matches('[data-option-index]')) sync();
    });
    sync();

    if (stickyBtn && variantInput) {
      stickyBtn.addEventListener('click', function () {
        if (stickyBtn.disabled) return;
        // pdp4-0210: senza taglia scelta la barra chiede la scelta (foglio dal basso su mobile, taglie evidenziate su desktop)
        if (!variantInput.value) { bar.dispatchEvent(new CustomEvent('studio:sticky-needs-option')); return; }
        addToCart(variantInput.value, 1, stickyBtn);
      });
    }

    if ('IntersectionObserver' in window) {
      // Visible only once the buy-buttons block has scrolled out of view UPWARD,
      // and only while the footer is not in view.
      var buyButtons = form.querySelector('[data-buy-buttons]') || mainAtc;
      var footer = document.querySelector('.studio-footer');
      var passedButtons = false;
      var footerInView = false;
      function updateStickyVisibility() {
        var show = passedButtons && !footerInView;
        bar.classList.toggle('studio-sticky-atc--visible', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        // pdp-0210: altezza della barra visibile, per tenere WhatsApp sopra
        document.documentElement.style.setProperty('--studio-sticky-h', show ? bar.offsetHeight + 'px' : '0px');
      }
      if (buyButtons) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            passedButtons = !en.isIntersecting && en.boundingClientRect.top < 0;
            updateStickyVisibility();
          });
        }, { threshold: 0 }).observe(buyButtons);
      }
      if (footer) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            footerInView = en.isIntersecting;
            updateStickyVisibility();
          });
        }, { threshold: 0 }).observe(footer);
      }
    }
  }

  /* ================================================== COUNTDOWN */
  var _countdownTicker = null;
  var _countdownEls = [];

  function tickCountdowns() {
    var now = Date.now();
    _countdownEls = _countdownEls.filter(function (el) {
      return document.documentElement.contains(el);
    });
    if (!_countdownEls.length) {
      clearInterval(_countdownTicker);
      _countdownTicker = null;
      return;
    }
    _countdownEls.forEach(function (el) {
      var end = parseInt(el.dataset.endTs, 10);
      var diff = Math.max(0, end - now);
      var s = Math.floor(diff / 1000);
      var d = Math.floor(s / 86400);
      var h = Math.floor((s % 86400) / 3600);
      var m = Math.floor((s % 3600) / 60);
      var sec = s % 60;
      function pad(n) { return String(n).padStart(2, '0'); }
      var units = { days: d, hours: h, minutes: m, seconds: sec };
      Object.keys(units).forEach(function (key) {
        var span = el.querySelector('[data-unit="' + key + '"]');
        if (span) span.textContent = pad(units[key]);
      });
      if (diff === 0 && !el.hasAttribute('data-expired')) {
        el.setAttribute('data-expired', '');
        var msg = el.querySelector('[data-expired-msg]');
        if (msg) msg.hidden = false;
      }
    });
  }

  function initCountdown(root) {
    $all('[data-countdown]', root || document).forEach(function (el) {
      if (!once(el, 'studioCountdownBound')) return;
      var raw = (el.dataset.endDate || '').trim();
      if (!raw) return;
      var ts = Date.parse(raw);
      if (isNaN(ts)) return;
      el.dataset.endTs = String(ts);
      _countdownEls.push(el);
    });
    if (_countdownEls.length && !_countdownTicker) {
      tickCountdowns();
      _countdownTicker = setInterval(tickCountdowns, 1000);
    }
  }

  /* ---- Background videos: play quando nel viewport, pausa fuori schermo. Salta i video in
     display:none (breakpoint sbagliato) -> non scaricano. Niente play se prefers-reduced-motion. ---- */
  function playPauseBgVideo(v, inView) {
    if (REDUCED_MOTION) { try { v.pause(); } catch (e) {} return; }
    var displayed = v.offsetParent !== null; /* null => display:none */
    if (inView && displayed) { try { v.play(); } catch (e) {} }
    else { try { v.pause(); } catch (e) {} }
  }

  function initBgVideos(root) {
    $all('[data-bg-video]', root || document).forEach(function (v) {
      if (!once(v, 'studioBgVideoBound')) return;
      if (!('IntersectionObserver' in window)) { playPauseBgVideo(v, true); return; }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          entry.target._bgInView = entry.isIntersecting;
          playPauseBgVideo(entry.target, entry.isIntersecting);
        });
      }, { threshold: 0.1 });
      io.observe(v);
    });
    /* un solo listener resize: al flip di breakpoint rivaluta quale <video> e' mostrato */
    if (once(document.body, 'studioBgVideoResize')) {
      window.addEventListener('resize', function () {
        $all('[data-bg-video]').forEach(function (v) { playPauseBgVideo(v, !!v._bgInView); });
      });
    }
  }

  /* ============================================================== BOOT */
  function initSection(scope) {
    initHeader(scope);
    initAccordion(scope);
    initSliders(scope);
    initHeadingAnimations(scope);
    loadCartRecommendations(scope);
    initStickyAtc();
    initProductRecommendations(scope);
    initCountdown(scope);
    initBgVideos(scope);
    initStatsCounters(scope);
  }

  /* ----------------------------------------------------- localization */
  function initLocalization() {
    if (!once(document.body, 'studioLocBound')) return;

    function closeLoc(root) {
      if (!root) return;
      var list = $('[data-loc-list]', root);
      var toggle = $('[data-loc-toggle]', root);
      if (list) list.hidden = true;
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      if (root._locTrap) { root._locTrap.deactivate(); root._locTrap = null; }
    }

    function closeAll(except) {
      $all('[data-loc]').forEach(function (root) {
        if (root !== except) closeLoc(root);
      });
    }

    document.addEventListener('click', function (e) {
      var toggle = e.target.closest('[data-loc-toggle]');
      if (toggle) {
        var root = toggle.closest('[data-loc]');
        if (!root) return;
        var list = $('[data-loc-list]', root);
        if (!list) return;
        var willOpen = list.hidden;
        closeAll(root);
        list.hidden = !willOpen;
        toggle.setAttribute('aria-expanded', String(willOpen));
        if (willOpen) {
          root._locTrap = new FocusTrap(list);
          root._locTrap.activate(toggle);
        } else if (root._locTrap) {
          root._locTrap.deactivate();
          root._locTrap = null;
        }
        return;
      }

      var option = e.target.closest('[data-loc-option], [data-loc-value]');
      if (option && option.hasAttribute('data-loc-value')) {
        var root2 = option.closest('[data-loc]');
        if (!root2) return;
        var form = $('[data-loc-input]', root2);
        form = form ? form.form : null;
        var input = $('[data-loc-input]', root2);
        if (form && input) {
          input.value = option.getAttribute('data-loc-value');
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
          } else {
            form.submit();
          }
        }
        return;
      }

      // Click outside any selector closes open dropdowns.
      if (!e.target.closest('[data-loc]')) closeAll(null);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        var open = $all('[data-loc]').filter(function (root) {
          var list = $('[data-loc-list]', root);
          return list && !list.hidden;
        });
        if (open.length) {
          open.forEach(function (root) { closeLoc(root); });
        }
      }
    });
  }

  /* --------------------------------------------------------- wishlist */
  function initWishlist() {
    if (!once(document.body, 'studioWishlistBound')) return;

    var KEY = 'studio:wishlist';
    // A valid handle is lowercase alphanumerics + hyphens (Shopify handle format).
    // Legacy numeric ids from step 1 fail this test and are discarded silently.
    var HANDLE_RE = /^[a-z0-9][a-z0-9\-]*$/;

    function isHandle(v) {
      return typeof v === 'string' && HANDLE_RE.test(v);
    }

    function getWishlist() {
      try {
        var raw = window.localStorage.getItem(KEY);
        if (!raw) return [];
        var arr = JSON.parse(raw);
        if (!Array.isArray(arr)) return [];
        return arr.map(String).filter(isHandle); // drop legacy numeric ids
      } catch (e) {
        return [];
      }
    }

    function saveWishlist(arr) {
      try {
        window.localStorage.setItem(KEY, JSON.stringify(arr));
      } catch (e) {
        /* private mode / storage unavailable — degrade silently */
      }
    }

    function isInWishlist(handle) {
      return getWishlist().indexOf(String(handle)) !== -1;
    }

    function toggleWishlist(handle) {
      handle = String(handle);
      if (!isHandle(handle)) return getWishlist();
      var arr = getWishlist();
      var i = arr.indexOf(handle);
      if (i === -1) { arr.push(handle); } else { arr.splice(i, 1); }
      saveWishlist(arr);
      return arr;
    }

    function updateWishlistCount() {
      var n = getWishlist().length;
      $all('[data-wishlist-count]').forEach(function (el) {
        el.textContent = String(n);
        el.style.display = n > 0 ? '' : 'none';
      });
    }

    function setButtonState(btn, active) {
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    }

    function btnHandle(btn) {
      // Prefer handle; tolerate cards that only expose it via data-product-handle.
      return btn.getAttribute('data-product-handle');
    }

    function syncButtons() {
      $all('[data-wishlist-toggle]').forEach(function (btn) {
        var h = btnHandle(btn);
        if (h) setButtonState(btn, isInWishlist(h));
      });
    }

    /* ---------- wishlist PAGE (Section Rendering API) ---------- */
    function getPage() { return $('[data-wishlist-page]'); }

    function renderPage() {
      var page = getPage();
      if (!page) return;
      var grid = $('[data-wishlist-grid]', page);
      var empty = $('[data-wishlist-empty]', page);
      var cardSection = page.getAttribute('data-wishlist-card-section') || 'wishlist-card';
      var handles = getWishlist();

      function showEmpty() {
        if (empty) empty.hidden = false;
        if (grid) { grid.hidden = true; grid.innerHTML = ''; }
      }
      function showGrid() {
        if (empty) empty.hidden = true;
        if (grid) grid.hidden = false;
      }

      if (!handles.length) { showEmpty(); return; }
      if (!grid) { return; }

      showGrid();
      grid.innerHTML = '';
      grid.setAttribute('aria-busy', 'true');

      // Ordered placeholders keep cards in the saved order regardless of which
      // fetch resolves first; each is filled or removed when its request lands.
      var slots = handles.map(function (handle) {
        var slot = document.createElement('div');
        slot.className = 'studio-wishlist__slot';
        grid.appendChild(slot);
        return { handle: handle, slot: slot };
      });

      function extractCard(text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        return doc.querySelector('.studio-card') ||
               doc.querySelector('[data-product-card]') ||
               (doc.body ? doc.body.firstElementChild : null);
      }

      function finalize() {
        grid.removeAttribute('aria-busy');
        // Unwrap any filled slots so the grid's own layout applies to the cards.
        $all('.studio-wishlist__slot', grid).forEach(function (slot) {
          if (slot.firstElementChild) {
            while (slot.firstChild) { grid.insertBefore(slot.firstChild, slot); }
          }
          grid.removeChild(slot);
        });
        if (!grid.children.length) { showEmpty(); return; }
        syncButtons();
        updateWishlistCount();
      }

      var pending = slots.length;
      function settle() { pending -= 1; if (pending <= 0) finalize(); }

      slots.forEach(function (entry) {
        fetch('/products/' + encodeURIComponent(entry.handle) + '?section_id=' + encodeURIComponent(cardSection))
          .then(function (r) { if (!r.ok) throw new Error('404'); return r.text(); })
          .then(function (text) {
            var card = extractCard(text);
            if (card) { entry.slot.appendChild(card); }
            settle();
          })
          .catch(function () {
            // Handle no longer exists / product unpublished — skip silently.
            settle();
          });
      });
    }

    /* ---------- click handling ---------- */
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      var h = btnHandle(btn);
      if (!h) return;
      e.preventDefault();
      toggleWishlist(h);
      setButtonState(btn, isInWishlist(h));
      updateWishlistCount();

      // If on the wishlist page and the item was removed, drop its card and
      // re-evaluate the empty state.
      var page = getPage();
      if (page && !isInWishlist(h)) {
        var card = btn.closest('.studio-card, .studio-wishlist__item');
        if (card && card.parentNode) card.parentNode.removeChild(card);
        var grid = $('[data-wishlist-grid]', page);
        var empty = $('[data-wishlist-empty]', page);
        if (grid && !grid.children.length) {
          grid.hidden = true;
          if (empty) empty.hidden = false;
        }
      }
    });

    // Initial sync: Liquid-rendered cards don't know the localStorage state.
    syncButtons();
    updateWishlistCount();
    renderPage();

    // Re-sync buttons that appear later (faceting, quick view, recommendations).
    document.addEventListener('shopify:section:load', function () {
      syncButtons();
      updateWishlistCount();
      renderPage();
    });
  }

  // Photo gallery (studio_card_swipe) for product cards AND quick view: swipe is pure CSS scroll-snap; this only
  // keeps the dots in sync. Document-delegated (scroll in CAPTURE phase + click) so it covers injected/QV markup,
  // and idempotent. Structure-based (gallery.parentElement) + shared hooks data-card-gallery / data-card-dot and
  // the shared 'is-active' state -> works for both .studio-card__* and .studio-qv__*, never touches the PDP gallery.
  function syncCardDots(gallery) {
    var media = gallery.parentElement;
    if (!media) return;
    var dots = media.querySelectorAll('[data-card-dot]');
    if (!dots.length) return;
    var idx = Math.round(gallery.scrollLeft / (gallery.clientWidth || 1));
    for (var i = 0; i < dots.length; i++) {
      dots[i].classList.toggle('is-active', i === idx);
    }
  }
  function initCardGalleries() {
    if (!once(document.body, 'studioCardGalleryBound')) return;
    document.addEventListener('scroll', function (e) {
      var g = e.target;
      if (g && g.hasAttribute && g.hasAttribute('data-card-gallery')) syncCardDots(g);
    }, true);
    document.addEventListener('click', function (e) {
      var dot = e.target.closest ? e.target.closest('[data-card-dot]') : null;
      if (!dot) return;
      var media = dot.parentElement && dot.parentElement.parentElement;
      var g = media && media.querySelector('[data-card-gallery]');
      if (!g) return;
      var idx = parseInt(dot.dataset.dot, 10) || 0;
      g.scrollTo({ left: idx * g.clientWidth, behavior: REDUCED_MOTION ? 'auto' : 'smooth' });
    });
  }

  // After a failed customer-form submit the page reloads with the error summary present (role=alert + tabindex=-1).
  // Move focus to it so keyboard / screen-reader users land on the errors. Scoped to account pages; idempotent.
  function initFormErrors() {
    if (!once(document.body, 'studioFormErrorsBound')) return;
    var box = document.querySelector('.studio-account .studio-form__errors');
    if (box) { try { box.focus(); } catch (e) {} }
  }

  function boot() {
    initCartDelegation();
    initSection(document);
    initPageTransition();
    initFacets();
    initPredictiveSearch();
    initCardSwatches();
    initCardGalleries();
    initQuickView();
    initLocalization();
    initWishlist();
    initFormErrors();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* --------------------------------------------- Theme Editor support */
  document.addEventListener('shopify:section:load', function (e) { initSection(e.target); });
  document.addEventListener('shopify:section:select', function (e) {
    if (e.target.querySelector && e.target.querySelector('.studio-cart-drawer')) openCart();
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    if (e.target.querySelector && e.target.querySelector('.studio-cart-drawer')) closeCart();
  });
})();